import { useEffect, useRef, type ReactNode } from "react";
import TextareaAutosize from "react-textarea-autosize";
import { twMerge } from "tailwind-merge";
import { Paperclip, Plus, X } from "lucide-react";
import { useComposerSelector } from "../../composer/useComposerSelector";
import { BaseDropZone } from "../attachments/BaseDropZone";
import { BaseAttachmentList } from "../attachments/BaseAttachmentList";
import { ImageThumbnail } from "../attachments/ImageThumbnail";
import { toDisplayAttachment } from "../attachments/types";
import { useRecommendations } from "./RecommendationsContext";
import type {
  CollectedValue,
  ParsedRecommendationField,
  Recommendation,
  RecommendationPanelProps,
} from "./types";

// ─────────────────────────────────────────────────────────────────────────
// RecommendationComposer — the transform surface
// ─────────────────────────────────────────────────────────────────────────

export interface RecommendationComposerProps {
  /** The surface's normal input — rendered whenever no panel is active. */
  children: ReactNode;
  className?: string;
  /** Hint line below an open panel. Pass null to hide. */
  panelHint?: ReactNode;
}

/**
 * Wrap the chat's input area with this to enable the composer transform:
 * with no active recommendation it renders `children` (the normal input)
 * untouched; when a recommendation panel is open, the input area becomes
 * the panel — and it is STILL the chat: the freeform note, attach button,
 * and drag-and-drop all keep working inside it.
 */
export function RecommendationComposer({
  children,
  className,
  panelHint = "This is still your chat — type, attach, or drag images. Press esc to collapse.",
}: RecommendationComposerProps) {
  const { active } = useRecommendations();

  if (!active) {
    return <>{children}</>;
  }
  return (
    <>
      <RecommendationPanel recommendation={active} className={className} />
      {panelHint && <ComposerHint>{panelHint}</ComposerHint>}
    </>
  );
}

function ComposerHint({ children }: { children: ReactNode }) {
  return (
    <p className="pt-1.5 text-center font-mono text-[11px] text-neutral-400">
      {children}
    </p>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// The panel
// ─────────────────────────────────────────────────────────────────────────

function RecommendationPanel({
  recommendation,
  className,
}: {
  recommendation: Recommendation;
  className?: string;
}) {
  const {
    close,
    cancel,
    submitActive,
    isSubmittable,
    descriptorFor,
    fields,
    collected,
    setField,
    addImages,
    attachmentsFor,
    removeAttachment,
    submitLabel,
    notePlaceholder,
  } = useRecommendations();
  const composer = useComposerSelector((s) => s);

  // esc collapses back to chat (state preserved) — from anywhere.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        close();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [close]);

  const descriptor = descriptorFor(recommendation);
  const BespokePanel = descriptor?.Panel;

  const panelProps: RecommendationPanelProps = {
    recommendation,
    fields,
    collected,
    setField,
    addImages,
    attachmentsFor,
    removeAttachment,
    submit: submitActive,
    cancel,
  };

  const untagged = composer.attachments.filter((a) => !a.tag);

  return (
    <BaseDropZone
      onDrop={(files) => composer.addFiles(files)}
      className={twMerge(
        "relative rounded-2xl border border-primary-200 bg-white shadow-sm",
        className,
      )}
    >
      <div data-testid="recommendation-panel" className="flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-neutral-100 p-3">
          <div className="flex items-center gap-3 min-w-0">
            {descriptor?.icon && (
              <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600 [&>svg]:size-4">
                {descriptor.icon}
              </span>
            )}
            <div className="min-w-0">
              <div className="text-sm font-semibold text-neutral-900">
                {recommendation.text}
              </div>
              {(descriptor?.description ?? recommendation.description) && (
                <div className="truncate text-xs text-neutral-500">
                  {descriptor?.description ?? recommendation.description}
                </div>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={close}
            data-testid="recommendation-panel-close"
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-neutral-200 px-2.5 py-1.5 text-xs text-neutral-500 transition-colors hover:bg-neutral-50"
          >
            <X className="size-3.5" />
            Back to chat
            <kbd className="rounded border border-neutral-200 bg-neutral-50 px-1 font-mono text-[10px]">
              esc
            </kbd>
          </button>
        </div>

        {/* Structured body — bespoke renderer or generic fields */}
        <div className="p-3">
          {BespokePanel ? (
            <BespokePanel {...panelProps} />
          ) : (
            <GenericFieldsPanel {...panelProps} />
          )}
        </div>

        {/* Persistent composer — still your chat */}
        <div className="flex flex-col gap-2 px-3 pb-3">
          {untagged.length > 0 && (
            <BaseAttachmentList
              attachments={untagged.map(toDisplayAttachment)}
              onRemove={composer.removeAttachment}
              className="flex flex-wrap gap-2"
            />
          )}
          <PanelNoteTextarea
            placeholder={descriptor?.notePlaceholder ?? notePlaceholder ?? "Add a note… (optional)"}
            onEnter={() => isSubmittable && submitActive()}
          />
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1">
              <PanelFileButton onFiles={(files) => composer.addFiles(files)} />
              <button
                type="button"
                onClick={cancel}
                data-testid="recommendation-panel-cancel"
                className="inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs text-neutral-500 transition-colors hover:bg-neutral-50"
              >
                <X className="size-3.5" />
                Cancel
              </button>
            </div>
            <button
              type="button"
              onClick={submitActive}
              disabled={!isSubmittable}
              data-testid="recommendation-panel-send"
              className="inline-flex items-center gap-2 rounded-xl bg-primary-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {descriptor?.submitLabel ?? submitLabel ?? "Send"}
            </button>
          </div>
        </div>
      </div>
    </BaseDropZone>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Footer primitives — bound to the composer, submit routed to the panel
// ─────────────────────────────────────────────────────────────────────────

function PanelNoteTextarea({
  placeholder,
  onEnter,
}: {
  placeholder: string;
  onEnter: () => void;
}) {
  const text = useComposerSelector((s) => s.text);
  const setText = useComposerSelector((s) => s.setText);
  return (
    <TextareaAutosize
      value={text}
      onChange={(e) => setText(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          onEnter();
        }
      }}
      placeholder={placeholder}
      minRows={1}
      maxRows={4}
      data-testid="recommendation-panel-note"
      className="w-full resize-none rounded-lg border border-neutral-200 bg-white px-3 py-2 font-sans text-sm text-neutral-800 placeholder:text-neutral-500 transition-colors hover:border-neutral-300 focus:border-primary-400 focus:outline-none focus:ring-0"
    />
  );
}

function PanelFileButton({ onFiles }: { onFiles: (files: FileList) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        aria-label="Attach files"
        className="rounded-lg p-1.5 text-neutral-500 transition-colors hover:bg-neutral-50"
      >
        <Paperclip className="size-4" />
      </button>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/png,image/jpeg,image/gif,image/webp,application/pdf"
        className="hidden"
        onChange={(e) => {
          if (e.target.files && e.target.files.length > 0) onFiles(e.target.files);
          e.target.value = "";
        }}
      />
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Generic field panel — the default structured body, driven by `collects`
// ─────────────────────────────────────────────────────────────────────────

export function GenericFieldsPanel({
  fields,
  collected,
  setField,
  addImages,
  attachmentsFor,
  removeAttachment,
}: RecommendationPanelProps) {
  if (fields.length === 0) return null;
  return (
    <div className="space-y-3">
      {fields.map((field) =>
        field.fields ? (
          <GroupFieldInput
            key={field.name}
            field={field}
            value={collected[field.name]}
            onChange={(records) => setField(field.name, records)}
          />
        ) : field.accepts === "image" ? (
          <ImageFieldInput
            key={field.name}
            field={field}
            addImages={addImages}
            attachmentsFor={attachmentsFor}
            removeAttachment={removeAttachment}
          />
        ) : field.accepts === "choice" ? (
          <ChoiceFieldInput
            key={field.name}
            field={field}
            value={typeof collected[field.name] === "string" ? (collected[field.name] as string) : field.default ?? ""}
            onChange={(option) => setField(field.name, option)}
          />
        ) : (
          <label key={field.name} className="block text-sm text-neutral-700">
            <FieldLabel field={field} />
            <input
              type="text"
              aria-label={field.prompt}
              placeholder={field.placeholder}
              value={typeof collected[field.name] === "string" ? (collected[field.name] as string) : ""}
              onChange={(e) => setField(field.name, e.target.value)}
              className="mt-1 w-full rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm placeholder:text-neutral-400 focus:border-neutral-300 focus:outline-none"
            />
          </label>
        ),
      )}
    </div>
  );
}

function FieldLabel({ field }: { field: ParsedRecommendationField }) {
  return (
    <span className="flex items-baseline gap-1.5">
      <span className="font-medium text-neutral-800">{field.prompt}</span>
      {field.optional && <span className="text-xs text-neutral-400">optional</span>}
    </span>
  );
}

/**
 * A group field — a repeatable record (an FAQ's {question, answer}, a
 * pricing tier's {title, price, description}). Renders one card per
 * record with + add / − remove; the collected value is the record array.
 */
function GroupFieldInput({
  field,
  value,
  onChange,
}: {
  field: ParsedRecommendationField;
  value: CollectedValue | undefined;
  onChange: (records: Array<Record<string, string>>) => void;
}) {
  const subfields = field.fields ?? [];
  const items: Array<Record<string, string>> = isRecordArray(value) ? value : [{}];
  const itemLabel = field.itemLabel ?? "item";

  const update = (index: number, sub: string, text: string) => {
    onChange(items.map((item, i) => (i === index ? { ...item, [sub]: text } : item)));
  };

  return (
    <div className="text-sm text-neutral-700">
      <FieldLabel field={field} />
      <div className="mt-1 space-y-2" data-testid={`group-field-${field.name}`}>
        {items.map((item, index) => (
          <div
            key={index}
            className="flex items-start gap-1 rounded-xl border border-neutral-200 bg-neutral-50 p-3"
          >
            <div className="min-w-0 flex-1 space-y-2">
              {subfields.map((sub) => (
                <input
                  key={sub.name}
                  type="text"
                  aria-label={`${capitalize(itemLabel)} ${index + 1} ${sub.prompt}`}
                  placeholder={sub.placeholder ?? sub.prompt}
                  value={item[sub.name] ?? ""}
                  onChange={(e) => update(index, sub.name, e.target.value)}
                  className="w-full rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm placeholder:text-neutral-400 focus:border-neutral-300 focus:outline-none"
                />
              ))}
            </div>
            {items.length > 1 && (
              <button
                type="button"
                aria-label={`Remove ${itemLabel} ${index + 1}`}
                onClick={() => onChange(items.filter((_, i) => i !== index))}
                className="-mr-1.5 -mt-1.5 rounded p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600"
              >
                <X className="size-4" />
              </button>
            )}
          </div>
        ))}
        <button
          type="button"
          onClick={() => onChange([...items, {}])}
          className="inline-flex items-center gap-1.5 rounded-lg bg-primary-50 px-3 py-2 text-sm font-medium text-primary-600 hover:bg-primary-100"
        >
          <Plus className="size-4" />
          Add {itemLabel}
        </button>
      </div>
    </div>
  );
}

function isRecordArray(value: unknown): value is Array<Record<string, string>> {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every((v) => v !== null && typeof v === "object" && !Array.isArray(v))
  );
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * A choice field — a segmented control over the spec's `options`. The
 * default is seeded into `collected` on panel open, so an untouched
 * control still submits its value.
 */
function ChoiceFieldInput({
  field,
  value,
  onChange,
}: {
  field: ParsedRecommendationField;
  value: string;
  onChange: (option: string) => void;
}) {
  return (
    <div className="text-sm text-neutral-700">
      <FieldLabel field={field} />
      <div
        role="radiogroup"
        aria-label={field.prompt}
        data-testid={`choice-field-${field.name}`}
        className="mt-1 inline-flex rounded-xl border border-neutral-200 bg-neutral-100 p-0.5"
      >
        {(field.options ?? []).map((option) => (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={value === option}
            onClick={() => onChange(option)}
            className={twMerge(
              "rounded-[10px] px-4 py-1.5 text-sm transition-colors",
              value === option
                ? "bg-white font-medium text-neutral-900 shadow-sm"
                : "text-neutral-500 hover:text-neutral-700",
            )}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * An image field — a drop square wired to the chat composer's upload
 * pipeline (tagged to this field), with thumbnails (upload spinners and
 * all) rendered from the composer's attachment state.
 */
function ImageFieldInput({
  field,
  addImages,
  attachmentsFor,
  removeAttachment,
}: {
  field: ParsedRecommendationField;
  addImages: RecommendationPanelProps["addImages"];
  attachmentsFor: RecommendationPanelProps["attachmentsFor"];
  removeAttachment: (id: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const attachments = attachmentsFor(field.name);

  const handleFiles = (files: FileList | File[]) => {
    const images = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (images.length === 0) return;
    addImages(field.name, field.multiple ? images : images.slice(0, 1));
  };

  return (
    <div className="text-sm text-neutral-700">
      <FieldLabel field={field} />
      <div
        role="button"
        tabIndex={0}
        data-testid={`image-field-${field.name}`}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => e.key === "Enter" && inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleFiles(e.dataTransfer.files);
        }}
        className="mt-1 flex cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-neutral-300 bg-neutral-50 px-3 py-6 text-center text-xs text-neutral-500 transition-colors hover:border-neutral-400"
      >
        <span>
          Drag photo{field.multiple ? "s" : ""} here{" "}
          <span className="text-neutral-400">or click to browse</span>
        </span>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple={field.multiple}
          className="hidden"
          onChange={(e) => {
            if (e.target.files) handleFiles(e.target.files);
            e.target.value = "";
          }}
        />
      </div>
      {attachments.length > 0 && (
        <div className="mt-2 flex flex-row flex-wrap items-start gap-2">
          {attachments.map((attachment) => (
            <ImageThumbnail
              key={attachment.id}
              attachment={toDisplayAttachment(attachment)}
              onRemove={removeAttachment}
            />
          ))}
        </div>
      )}
    </div>
  );
}
